// Utilities for working with text in the DOM
/* eslint no-param-reassign: 0 */
import {getFirstTextChild, getLastTextChild, isElement, isText} from '@sinclair-digital/sbg-util/dist/es/node';
import {Just, Nothing} from 'data.maybe';
import {chain, compose, flatten, is, lensPath, lift, map, over, pipe, reduce, uniq, when} from 'ramda';
import {fromMaybe, nonEmpty} from '@sinclair-digital/sbg-util/dist/es/list';
import * as El from '@sinclair-digital/sbg-util/dist/es/element';

import {dictUtf8ToHtmlEntities, dictHtmlEntitiesToUtf8, dictCaseInsensitiveHtmlEntitiesToUtf8, whiteSpaceEntities} from './text.data';
import {logEvent} from '../analytics/common';

const elementsWithMargin = new Set(['OL', 'UL', 'P', 'H1', 'H2', 'H3']);
const blockEls = new Set(['DIV', 'LI', 'BR', ...elementsWithMargin]);

// removes the trailing space from the caluculation of countText
// decRemainder :: [Int, Int] -> [Int, Int]
const decRemainder = ([lines, rem]) => [lines, rem - 1];

// Returns number of lines and remaining characters
// countText :: (Int, Int, String) -> [Int, Int]
export const countText = pipe(
    (charsPerLine, remainingChars, str) =>
        str.split(' ').reduce(
            ([lines, rem], word) => {
                if (word.length === 0) {
                    return [lines, rem];
                } else if (word.length + rem > charsPerLine) {
                    lines += Math.floor((rem + word.length) / charsPerLine);
                    rem = word.length > charsPerLine ? (rem + word.length) % charsPerLine : word.length;
                } else {
                    rem += word.length;
                }
                return [lines, rem + 1];
            },
            [0, remainingChars],
        ),
    decRemainder,
);

// _countLines :: (Int, Element, Int) -> [Int, Int]
// eslint-disable-next-line max-statements
export const _countLines = (charsPerLine, el, remainingChars) => {
    let lines = 0;
    let firstChild = true;
    for (const node of Array.from(el.childNodes)) {
        if (isText(node)) {
            const [_lines, _remainingChars] = countText(charsPerLine, remainingChars, node.nodeValue);
            remainingChars = _remainingChars;
            lines += _lines;
        } else if (isElement(node)) {
            // elements with margin add another line
            if (!firstChild) {
                if (elementsWithMargin.has(node.tagName)) lines += 1;
                if (node.tagName === 'BR' || (remainingChars && blockEls.has(node.tagName))) {
                    lines += Math.max(1, Math.ceil(remainingChars / charsPerLine));
                    remainingChars = 0;
                }
            }
            const [_lines, _remainingChars] = _countLines(charsPerLine, node, remainingChars);
            lines += _lines;
            remainingChars = _remainingChars;
        }
        firstChild = false;
    }
    return [lines, remainingChars];
};

// Counts potential lines of text in an element recursing to all children
// countLines :: (Int, Element) -> Int
export const countLines = (charsPerLine, el) => {
    const [lines, charsInCurrentBlock] = _countLines(charsPerLine, el, 0);
    return charsInCurrentBlock >= 1 ? lines + Math.ceil(charsInCurrentBlock / charsPerLine) : lines;
};

let extendCounter = 0;

// divide text among two elements based upon max number of characters
// mutative
// splitText :: (Int, Element, Element, TextNode, Int, Int) -> [Int, Int]
// eslint-disable-next-line max-statements
const splitText = (charsPerLine, fst, snd, txt, linesLeft, charsLeft) => {
    // text of text node would overflow, dive deeper
    const words = txt.nodeValue.split(' ');
    let i = 0;
    for (const w of words) {
        const isMoreThanOneLine = charsLeft + w.length > charsPerLine;
        if (isMoreThanOneLine) {
            const _linesLeft = linesLeft;
            linesLeft -= Math.floor((charsLeft + w.length) / charsPerLine);

            // if word is greater than line length break it up
            if (linesLeft <= 0) {
                if (w.length > charsPerLine) {
                    const at = _linesLeft * charsPerLine - charsLeft;
                    El.append(words.slice(0, i).join(' ') + ' ' + w.slice(0, at), fst);
                    El.append(w.slice(at) + ' ' + words.slice(i + 1).join(' '), snd);
                } else {
                    El.append(words.slice(0, i).join(' '), fst);
                    El.append(words.slice(i).join(' '), snd);
                }
                extendCounter += 1;
                fst.setAttribute('data-extendstart', extendCounter);
                snd.setAttribute('data-extendend', extendCounter);
                charsLeft = 0;
                break;
            }
            charsLeft = 0;
        }
        charsLeft += w.length + 1;
        i += 1;
    }
    if (linesLeft > 0) {
        fst.appendChild(txt);
    }
    return [linesLeft, charsLeft];
};

// nonEmptyAndNotBR :: Element -> Maybe Element
const nonEmptyAndNotBR = el => El.nonEmpty(el).orElse(() => (el.tagName === 'BR' ? Just(el) : Nothing()));

// mutative
// splitLines :: (Int, Int, Element, Int) -> [Maybe Element, Maybe Element, Int, Int]
// eslint-disable-next-line complexity, max-statements
export const splitLines = (charsPerLine, el, linesLeft, charsLeft = 0) => {
    const children = Array.from(el.childNodes);
    if (children.length === 0) {
        return [nonEmptyAndNotBR(el), Nothing(), linesLeft, charsLeft];
    } else if (linesLeft <= 0) {
        return [Nothing(), nonEmptyAndNotBR(el), 0, 0];
    }
    const fst = el.cloneNode(false);
    const snd = el.cloneNode(false);
    let firstChild = true;
    for (const node of children) {
        if (linesLeft <= 0) {
            snd.appendChild(node.cloneNode(true));
        } else if (isText(node)) {
            [linesLeft, charsLeft] = splitText(charsPerLine, fst, snd, node, linesLeft, charsLeft);
        } else if (isElement(node)) {
            if (!firstChild) {
                if (elementsWithMargin.has(node.tagName)) {
                    linesLeft -= 1;
                }
                if (node.tagName === 'BR' || (charsLeft > 0 && blockEls.has(node.tagName))) {
                    linesLeft -= 1;
                    charsLeft = 0;
                }
            }
            const [a, b, _linesLeft, _charsLeft] = splitLines(charsPerLine, node, linesLeft, charsLeft);
            charsLeft = _charsLeft;
            linesLeft = _linesLeft;
            a.map(aEl => fst.appendChild(aEl));
            b.map(bEl => snd.appendChild(bEl)); //eslint-disable-line array-callback-return, no-loop-func
        }
        firstChild = false;
    }
    return [nonEmptyAndNotBR(fst), nonEmptyAndNotBR(snd), linesLeft, charsLeft];
};

// mutative
// splitLinesAll :: (Int, Int) -> Element -> [Element]
export const splitLinesAll = (charsPerLine, maxLines) => el => {
    const [fst, snd] = splitLines(charsPerLine, el, maxLines);
    const lines = fromMaybe(fst);
    const sndLines = flatten(fromMaybe(snd.map(splitLinesAll(charsPerLine, maxLines))));
    return lines.concat(sndLines);
};

// mutative
// joinText :: (TextNode, TextNode) -> TextNode
const joinText = (targetText, srcText) => {
    const targetParent = targetText.parentNode;
    const srcParent = srcText.parentNode;
    if (
        targetParent.hasAttribute('data-extendstart') &&
        srcParent.hasAttribute('data-extendend') &&
        targetParent.getAttribute('data-extendstart') === srcParent.getAttribute('data-extendend')
    ) {
        // unify selection between these nodes if applicable
        targetText.nodeValue += ' ' + srcText.nodeValue;
        const parent = srcText.parentNode;
        parent.removeChild(srcText);
        if (El.isEmpty(parent)) {
            parent.remove();
        } else {
            parent.removeAttribute('data-extendend');
        }
        targetText.parentNode.removeAttribute('data-extendstart');
    }
    return targetText;
};

// mutative
// joinElements :: NonEmptyList Element -> NonEmptyList Element
export const joinElements = ([head, ...tail]) =>
    tail.reduce(
        (acc, src) => {
            const target = acc[acc.length - 1];
            lift(joinText)(getLastTextChild(target), getFirstTextChild(src));
            return El.isEmpty(src) ? acc : acc.concat([src]);
        },
        [head],
    );

// mutative
// addMixedNodes :: ([Element], [Node]) -> [Element]
const addMixedNodes = (els, nodes) => {
    if (nodes.length) {
        els.push(El.H.span({}, nodes));
    }
    return els;
};

// Wraps non-block elements and <br> with a <span> and pushes onto Element array.
// groupMixedNodes :: ([Element], [Node]) -> [[Element], [Node]]
const groupMixedNodes = ([els, mixedNodes], node) => {
    if (isElement(node) && blockEls.has(node.tagName) && node.tagName !== 'BR') {
        addMixedNodes(els, mixedNodes);
        els.push(node);
        return [els, []];
    }
    mixedNodes.push(node);
    return [els, mixedNodes];
};

// wraps all non-block, text nodes, and <br>s with a span.
// wrapMixedNodes :: [Element] -> [Element]
const wrapMixedNodes = els => addMixedNodes(...els.reduce(groupMixedNodes, [[], []]));

// mutative
// joinAllElements :: [Element] -> Maybe Element
export const joinAllElements = pipe(
    nonEmpty,
    map(els => {
        const children = chain(el => wrapMixedNodes(Array.from(el.childNodes)), els);
        const joined = joinElements(children);
        return El.appendAll(joined, El.empty(els[0]));
    }),
);

const filterAttrs = (tagSpec, child) => {
    if (typeof tagSpec !== 'object' || tagSpec == null) {
        tagSpec = {};
    }
    for (let j = child.attributes.length - 1; j >= 0; j--) {
        const attr = child.attributes[j];
        const attrName = attr.name;
        if (!(attrName in tagSpec)) {
            //Doesn't cover attrs from different namespaces.
            child.removeAttribute(attrName);
            continue;
        }
        if (!(tagSpec[attrName] instanceof RegExp)) {
            continue;
        }
        const match = attr.value.match(tagSpec[attrName]);
        if (!match) {
            child.removeAttribute(attrName);
            continue;
        }
        if (match.length > 1) {
            attr.value = match.slice(1).join('');
        }
    }
};

const filterChildren = (tagsSpec, node) => {
    for (let i = 0; i < node.childNodes.length; i++) {
        const child = node.childNodes[i];
        if (child.nodeType !== Node.ELEMENT_NODE) {
            continue;
        }
        const tagName = child.tagName.toLowerCase();
        if (!(tagName in tagsSpec)) {
            node.replaceChild(document.createTextNode(child.textContent), child);
            continue;
        }
        filterAttrs(tagsSpec[tagName], child);
        filterChildren(tagsSpec, child);
    }
};

// filterHtml :: ({tagname: {attrName: RegExp | *} | *}, String) -> String
export const filterHtml = (tagsSpec, html) => {
    const root = document.createElement('div');
    //<script>s aren't executed, onload="" and onerror="" too (nothing is loaded until attached).
    root.innerHTML = html;
    filterChildren(tagsSpec, root);
    return root.innerHTML;
};

// These are no longer being used. Were they ever? (N. Irvin)
export const richTextRegex = /{[^}]*}?/g;
export const stripRichText = (s = '') => {
    console.warn('deprecated function, will be removed eventually.');
    logEvent({
        category: 'RichText Editor',
        action: 'Deprecated function called',
        label: 'stripRichText',
    });
    return s.replace(richTextRegex, '');
};


export const lookupUtf8ToHtmlEntities = (char) => dictUtf8ToHtmlEntities[char] || char;
export const lookupHtmlEntitiesToUtf8 = (entity) =>
    dictHtmlEntitiesToUtf8[entity] ||
    dictCaseInsensitiveHtmlEntitiesToUtf8[entity.toLowerCase()] ||
    entity;

const reHtmlEntity = /(&[A-z0-9#]+;)/g;

const extractEntities =
    compose(
        uniq,
        map(match => match[0]),
        str => [...str.matchAll(reHtmlEntity)]
    );

export const htmlEntitiesToUtf8 = str =>
    pipe(
        extractEntities,
        reduce(
            (acc, entity) => {
                const utfOutput = lookupHtmlEntitiesToUtf8(entity);

                if (typeof acc.replaceAll !== 'function') {
                    return acc.replace(new RegExp(entity, 'g'), utfOutput);
                }

                return acc.replaceAll(entity, utfOutput);
            },
            str
        )
    )(str);

export const utf8ToHtmlEntities = str =>
    htmlEntitiesToUtf8(str)
        .split('')
        .map((char, index) =>
            (char === '&' && whiteSpaceEntities.some(entity => str.startsWith(entity, index))) ? '&' : lookupUtf8ToHtmlEntities(char)
        )
        .join('');

const createApplyStringPaths = fn => {
    const applyFn = when(is(String), fn);

    return stringPaths => obj =>
        stringPaths.reduce(
            (acc, stringPath) => over(lensPath(stringPath), applyFn, acc),
            obj
        );
};

export const createEscapeObject = createApplyStringPaths(utf8ToHtmlEntities);
export const createUnescapeObject = createApplyStringPaths(htmlEntitiesToUtf8);
